import {
	inferGraphTimeZone,
	mergeGraphLayoutWithOffsets,
	RELATIONSHIP_GRAPH_SCHEMA_VERSION,
	type GraphDocument,
	type GraphNodeOffset,
} from '../../../core/entities/graph-document';
import type { GraphAIAssistConfig } from '../../../core/entities/ai-assist-config';
import type { GraphAppearanceConfig } from '../../../core/entities/graph-appearance-config';
import { currentTimeZone } from '../../../core/entities/node';
import type { MediaRepository } from '../../../core/ports/media-repository';
import { serializeJourneyNodesArchive } from '../../../storage/archive/journeynodes-serializer';
import type { ArchiveSignature, GraphArchivePackage } from '../../../types/archive';

export interface ExportGraphOptions {
	archiveTitle?: string;
	transientNodeOffsets?: Record<string, GraphNodeOffset>;
	aiAssistConfig?: GraphAIAssistConfig;
	graphAppearanceConfig?: GraphAppearanceConfig;
	archiveSigner?: ArchiveSigningClient;
}

export interface ArchiveSigningClient {
	sign(archive: GraphArchivePackage): Promise<ArchiveSignature>;
}

export class ArchiveTransferService {
	constructor(private mediaRepository: MediaRepository, private archiveSigner?: ArchiveSigningClient) {}

	async exportGraph(graph: GraphDocument, options: ExportGraphOptions = {}): Promise<Blob> {
		const media = await this.mediaRepository.list();
		const exportedAt = new Date().toISOString();
		const exportTimeZone = currentTimeZone();
		const archiveTimeZone = inferGraphTimeZone(graph);
		const nodes = graph.nodes.map((node) => ({
			...node,
			createdAtTimeZone: node.createdAtTimeZone ?? archiveTimeZone,
			updatedAtTimeZone: node.updatedAtTimeZone ?? node.createdAtTimeZone ?? archiveTimeZone,
		}));
		const graphJson: GraphDocument = {
			...graph,
			title: options.archiveTitle ?? graph.title,
			schemaVersion: RELATIONSHIP_GRAPH_SCHEMA_VERSION,
			timestampOriginTimeZone: graph.timestampOriginTimeZone ?? archiveTimeZone,
			createdAtTimeZone: graph.createdAtTimeZone ?? archiveTimeZone,
			updatedAtTimeZone: exportTimeZone,
			layout: mergeGraphLayoutWithOffsets(graph.layout, options.transientNodeOffsets ?? {}),
			nodes,
			connections: graph.connections.map((connection) => ({
				...connection,
				createdAtTimeZone: connection.createdAtTimeZone ?? archiveTimeZone,
				updatedAtTimeZone: connection.updatedAtTimeZone ?? connection.createdAtTimeZone ?? archiveTimeZone,
			})),
			relationships: (graph.relationships ?? []).map((relationshipPair) => ({
				...relationshipPair,
				createdAtTimeZone: relationshipPair.createdAtTimeZone ?? archiveTimeZone,
				updatedAtTimeZone: relationshipPair.updatedAtTimeZone ?? relationshipPair.createdAtTimeZone ?? archiveTimeZone,
				directions: relationshipPair.directions.map((direction) => ({
					...direction,
					labels: [...direction.labels],
				})) as typeof relationshipPair.directions,
			})),
			updatedAt: exportedAt,
		};

		const archivePackage: GraphArchivePackage = {
			graphJson,
			mediaManifest: { schemaVersion: '1.0.0', assets: media.map((item) => item.metadata) },
			assets: Object.fromEntries(media.map((item) => [item.metadata.filename, item.data])),
			configJson:
				options.aiAssistConfig || options.graphAppearanceConfig
					? {
						...(options.aiAssistConfig ? { aiAssistConfig: options.aiAssistConfig } : {}),
						...(options.graphAppearanceConfig ? { graphAppearanceConfig: options.graphAppearanceConfig } : {}),
					}
					: undefined,
		};

		const signer = options.archiveSigner ?? this.archiveSigner;
		if (graphJson.nodes.length > 10) {
			if (!signer) {
				throw new Error('A Worker signed archive is required before exporting more than 10 nodes.');
			}
			archivePackage.archiveSignature = await signer.sign(archivePackage);
		}

		return serializeJourneyNodesArchive(archivePackage);
	}
}
